import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../store/services/type';
import { authLogout } from '../store/actions/authActions';
import * as authServices from '../store/services/authServices';

const PRIMARY = '#2B9A99';
const PRIMARY_HOVER = '#238585';

const PLANS = [
  {
    name: 'Standard',
    price: '£69',
    period: '/month',
    description: 'Perfect for retailers focusing on core mobility products.',
    features: ['Wheelchairs', 'Scooters', 'Walkers', 'Email support'],
    priceId: 'price_1QVWyJEKs5giB7h5c4weAUGm',
    highlight: false,
  },
  {
    name: 'Professional',
    price: '£99',
    period: '/month',
    description: 'Full product range for comprehensive mobility retailers.',
    features: ['All Standard products', 'Riser Recliner Chairs', 'Priority support'],
    priceId: 'price_1QVWzGEKs5giB7h5r4jkOG94',
    highlight: true,
  },
];

function CheckIcon({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2 7l3.5 3.5L12 3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlanCard({ plan }) {
  const [hovered, setHovered] = React.useState(false);
  const [loading, setLoading] = useState(false);

  const handleSelect = async () => {
    setLoading(true);
    try {
      const res = await axios.post(BASE_URL + 'checkout-session', { price_id: plan.priceId });
      window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
      alert('Something went wrong. Please try again.');
    }
  };

  const cardStyle = {
    position: 'relative',
    borderRadius: 16,
    border: `2px solid ${plan.highlight ? PRIMARY : '#e5e7eb'}`,
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    background: plan.highlight ? PRIMARY : '#fff',
    boxShadow: plan.highlight ? '0 8px 40px rgba(43,154,153,0.25)' : 'none',
  };

  const btnStyle = {
    display: 'block',
    width: '100%',
    padding: '12px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    textAlign: 'center',
    textDecoration: 'none',
    transition: 'all 0.2s',
    background: plan.highlight ? (hovered ? 'rgba(255,255,255,0.9)' : '#fff') : (hovered ? PRIMARY_HOVER : PRIMARY),
    color: plan.highlight ? PRIMARY : '#fff',
    border: 'none',
    cursor: 'pointer',
  };

  return (
    <div style={cardStyle}>
      {plan.highlight && (
        <div style={{
          position: 'absolute',
          top: -14,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#fff',
          color: PRIMARY,
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          padding: '4px 16px',
          borderRadius: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          whiteSpace: 'nowrap',
        }}>
          Most Popular
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: plan.highlight ? 'rgba(255,255,255,0.75)' : '#9ca3af' }}>
        {plan.name}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 12 }}>
        <span style={{ fontSize: 40, fontWeight: 800, lineHeight: 1, color: plan.highlight ? '#fff' : '#111827' }}>
          {plan.price}
        </span>
        {plan.period && (
          <span style={{ fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.6)' : '#9ca3af' }}>
            {plan.period}
          </span>
        )}
      </div>

      <p style={{ fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.75)' : '#6b7280', marginBottom: 24, lineHeight: 1.5 }}>
        {plan.description}
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', flex: 1 }}>
        {plan.features.map((f) => (
          <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: plan.highlight ? 'rgba(255,255,255,0.85)' : '#6b7280', padding: '7px 0', borderBottom: `1px solid ${plan.highlight ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
            <CheckIcon color={plan.highlight ? '#fff' : PRIMARY} />
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={handleSelect}
        disabled={loading}
        style={{ ...btnStyle, opacity: loading ? 0.7 : 1 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {loading ? 'Please wait...' : 'Get Started'}
      </button>
    </div>
  );
}

const PricingTable = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const handleLogout = () => {
    authServices.logout()
      .then(() => {
        dispatch(authLogout());
        localStorage.clear();
        history.push('/login');
      })
      .catch(() => {
        dispatch(authLogout());
        localStorage.clear();
        history.push('/login');
      });
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: '60px 20px' }}>
      <button
        onClick={handleLogout}
        style={{
          position: 'absolute',
          top: 20,
          right: 24,
          background: 'transparent',
          border: `1px solid #e5e7eb`,
          borderRadius: 8,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 600,
          color: '#6b7280',
          cursor: 'pointer',
        }}
      >
        Log out
      </button>
      <div style={{ maxWidth: 960, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <p style={{ color: PRIMARY, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Pricing</p>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#111827', marginBottom: 12 }}>Simple, transparent plans</h1>
          <p style={{ color: '#6b7280', fontSize: 16, maxWidth: 460, margin: '0 auto' }}>
            No setup fees, no hidden costs. Cancel anytime. The customer app is always free to use.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, alignItems: 'stretch', maxWidth: 680, margin: '0 auto' }}>
          {PLANS.map((plan) => (
            <PlanCard key={plan.name} plan={plan} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
